import { InterfacePageName } from '@uniswap/analytics-events'
import { ChainId, Currency } from '@uniswap/sdk-core'
import { useWeb3React } from '@web3-react/core'
import { Trace } from 'analytics'
import hero from 'assets/webp/hero.webp'
import { NetworkAlert } from 'components/NetworkAlert/NetworkAlert'
import { SwitchLocaleLink } from 'components/SwitchLocaleLink'
import SwapHeader from 'components/swap/SwapHeader'
import { SwapTab } from 'components/swap/constants'
import { SwapWrapper } from 'components/swap/styled'
import { DEFAULT_CHAIN_ID, asSupportedChain } from 'constants/chains'
import { useCurrency } from 'hooks/Tokens'
import useParsedQueryString from 'hooks/useParsedQueryString'
import { useScreenSize } from 'hooks/useScreenSize'
import { SendForm } from 'pages/Swap/Send/SendForm'
import { ReactNode, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { InterfaceTrade, TradeState } from 'state/routing/types'
import { isPreviewTrade } from 'state/routing/utils'
import { SwapAndLimitContextProvider, SwapContextProvider } from 'state/swap/SwapContext'
import { queryParametersToCurrencyState } from 'state/swap/hooks'
import { CurrencyState, SwapAndLimitContext } from 'state/swap/types'
import { useIsDarkMode } from '../../theme/components/ThemeToggle'
import { BenefitSection } from './BenefitSection'
import { FeatureSection } from './FeatureSection'
import { Footer } from './Footer'
import { LimitFormWrapper } from './Limit/LimitForm'
import { StatsSection } from './StatsSection'
import { SwapForm } from './SwapForm'

export function getIsReviewableQuote(
  trade: InterfaceTrade | undefined,
  tradeState: TradeState,
  swapInputError?: ReactNode
): boolean {
  if (swapInputError) return false
  // if the current quote is a preview quote, allow the user to progress to the Swap review screen
  if (isPreviewTrade(trade)) return true

  return Boolean(trade && tradeState === TradeState.VALID)
}

export default function SwapPage({ className }: { className?: string }) {
  const location = useLocation()

  const { chainId: connectedChainId } = useWeb3React()
  const supportedChainId = asSupportedChain(connectedChainId)
  const chainId = supportedChainId || DEFAULT_CHAIN_ID

  const parsedQs = useParsedQueryString()
  const parsedCurrencyState = useMemo(() => {
    return queryParametersToCurrencyState(parsedQs)
  }, [parsedQs])

  const initialInputCurrency = useCurrency(parsedCurrencyState.inputCurrencyId, chainId)
  const initialOutputCurrency = useCurrency(parsedCurrencyState.outputCurrencyId, chainId)

  return (
    <Trace page={InterfacePageName.SWAP_PAGE} shouldLogImpression>
      <div className="bg-[#F9F9F8] w-full">
        <div className="absolute left-0 right-0 top-[72px] flex items-center justify-center">
          <img src={hero} alt="hero" className="w-full h-full" width={1829} height={865} loading="lazy" />
        </div>
        <div className="relative">
          <div className="w-full grid grid-cols-1 lg:grid-cols-[repeat(auto-fit,_minmax(440px,_1fr))] items-center gap-10 xl:gap-16 2xl:gap-[100px] gap-8 px-5 xl:px-[160px] 2xl:px-[220px] mx-auto py-20 lg:py-[120px]">
            <div className="flex flex-col gap-2 lg:gap-6">
              <p className="text-[44px] lg:text-[54px] text-center lg:text-left leading-[48px] lg:leading-[62px] font-[450] text-black">
                Swap Anytime, Anywhere
              </p>

              <p className="text-center lg:text-left text-sm lg:text-lg text-light7">
                WorldSwap connects you to the Worldcoin ecosystem, prioritizing a human-first approach to finance.
              </p>
            </div>

            <div>
              <Swap
                className={className}
                chainId={chainId}
                disableTokenInputs={supportedChainId === undefined}
                initialInputCurrency={initialInputCurrency}
                initialOutputCurrency={initialOutputCurrency}
                syncTabToUrl={true}
              />
              <NetworkAlert />
            </div>
          </div>

          {location.pathname === '/swap' && <SwitchLocaleLink />}

          <FeatureSection />
          <BenefitSection />

          {/* <StatsSection /> */}

          <Footer />
        </div>
      </div>
    </Trace>
  )
}

/**
 * The swap component displays the swap interface, manages state for the swap, and triggers onchain swaps.
 *
 * In most cases, chainId should refer to the connected chain, i.e. `useWeb3React().chainId`.
 * However if this component is being used in a context that displays information from a different, unconnected
 * chain (e.g. the TDP), then chainId should refer to the unconnected chain.
 */
export function Swap({
  className,
  initialInputCurrency,
  initialOutputCurrency,
  chainId,
  onCurrencyChange,
  disableTokenInputs = false,
  compact = false,
  syncTabToUrl,
}: {
  className?: string
  chainId?: ChainId
  onCurrencyChange?: (selected: CurrencyState) => void
  disableTokenInputs?: boolean
  initialInputCurrency?: Currency
  initialOutputCurrency?: Currency
  compact?: boolean
  syncTabToUrl: boolean
}) {
  const isDark = useIsDarkMode()
  const screenSize = useScreenSize()

  return (
    <>
      <SwapAndLimitContextProvider
        chainId={chainId}
        initialInputCurrency={initialInputCurrency}
        initialOutputCurrency={initialOutputCurrency}
      >
        {/* TODO: Move SwapContextProvider inside Swap tab ONLY after SwapHeader removes references to trade / autoSlippage */}
        <SwapAndLimitContext.Consumer>
          {({ currentTab }) => (
            <SwapContextProvider>
              <SwapWrapper isDark={isDark} className={className} id="swap-page">
                <SwapHeader compact={compact || !screenSize.sm} syncTabToUrl={syncTabToUrl} />
                {currentTab === SwapTab.Swap && (
                  <SwapForm onCurrencyChange={onCurrencyChange} disableTokenInputs={disableTokenInputs} />
                )}
                {currentTab === SwapTab.Limit && <LimitFormWrapper onCurrencyChange={onCurrencyChange} />}
                {currentTab === SwapTab.Send && (
                  <SendForm disableTokenInputs={disableTokenInputs} onCurrencyChange={onCurrencyChange} />
                )}
              </SwapWrapper>
            </SwapContextProvider>
          )}
        </SwapAndLimitContext.Consumer>
      </SwapAndLimitContextProvider>
    </>
  )
}
